import { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Mail, Copy, ExternalLink, Loader2 } from 'lucide-react';

export default function SupplierEmail({ orderId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const generate = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/orders/${orderId}/email`);
            setData(r.data);
        } catch (e) {
            toast.error('Could not generate email');
        } finally {
            setLoading(false);
        }
    };

    const copyAll = async () => {
        if (!data) return;
        const text = `Subject: ${data.subject}\nTo: ${data.to}\n\n${data.body}`;
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied to clipboard');
        } catch {
            toast.error('Copy failed — select manually');
        }
    };

    if (!data) {
        return (
            <button
                data-testid={`generate-email-btn-${orderId}`}
                onClick={generate}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-brand hover:text-brand font-display uppercase text-xs tracking-wider disabled:opacity-50"
            >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {loading ? 'Generating…' : 'Generate Supplier Email'}
            </button>
        );
    }

    return (
        <div className="border border-zinc-800 bg-ink" data-testid={`supplier-email-${orderId}`}>
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-zinc-800 bg-surface">
                <div className="flex items-center gap-2 font-mono-data text-xs uppercase text-zinc-400">
                    <Mail size={14} className="text-brand" />
                    Supplier Order Email
                </div>
                <div className="flex gap-2">
                    <button
                        data-testid={`copy-email-btn-${orderId}`}
                        onClick={copyAll}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 hover:border-brand hover:text-brand font-display uppercase text-[10px] tracking-wider"
                    >
                        <Copy size={12} /> Copy
                    </button>
                    <a
                        data-testid={`mailto-btn-${orderId}`}
                        href={data.mailto}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand hover:bg-brand-hover text-black font-display uppercase text-[10px] tracking-wider"
                    >
                        <ExternalLink size={12} /> Open in Mail App
                    </a>
                </div>
            </div>
            <div className="px-4 py-3 border-b border-zinc-800/60 font-mono-data text-xs space-y-1">
                <div><span className="text-zinc-500">To:</span> <span className="text-zinc-200">{data.to}</span></div>
                <div><span className="text-zinc-500">Subject:</span> <span className="text-zinc-200">{data.subject}</span></div>
            </div>
            <pre className="px-4 py-4 font-mono-data text-xs text-zinc-300 whitespace-pre-wrap max-h-[400px] overflow-y-auto leading-relaxed">
{data.body}
            </pre>
        </div>
    );
}
